import { t } from "@lingui/core/macro"
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { GitCompare, History, X } from "lucide-react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { TranslationsSection } from "@/features/store-configuration/components/translations/TranslationsSection"
import { SUPPORTED_UI_LOCALES } from "@/features/store-configuration/constants/locales"
import {
  getConfigurationVersionFn,
  listConfigurationVersionsFn,
} from "@/features/store-configuration/server/storeConfiguration"
import { usePermissions } from "@/hooks/usePermissions"
import { storeConfigurationQueryKeys } from "@/lib/queryKeys"

const versionQuery = (versionId: string) =>
  queryOptions({
    queryKey: storeConfigurationQueryKeys.version(versionId),
    queryFn: () => getConfigurationVersionFn({ data: { id: versionId } }),
  })

const configurationVersionsListQuery = () =>
  queryOptions({
    queryKey: storeConfigurationQueryKeys.list({ includeArchived: false }),
    queryFn: () => listConfigurationVersionsFn({ data: { includeArchived: false } }),
  })

export const Route = createFileRoute(
  "/(authenticated)/settings/configuration-versions/$versionId/translations",
)({
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(versionQuery(params.versionId))
    void context.queryClient.ensureQueryData(configurationVersionsListQuery())
  },
  component: TranslationsPage,
})

function TranslationsPage() {
  const { versionId } = Route.useParams()
  const { data: versionData } = useSuspenseQuery(versionQuery(versionId))
  const { data: versions } = useSuspenseQuery(configurationVersionsListQuery())
  const { hasPermission } = usePermissions()
  const canUpdate = hasPermission("configVersion", "update").allowed

  const [comparisonVersionId, setComparisonVersionId] = useState<string | null>(null)
  const isComparing = comparisonVersionId !== null

  const otherVersions = versions.filter((v) => v.id !== versionId)

  const supportedLocales =
    (versionData?.supportedLocales as string[] | undefined) ?? SUPPORTED_UI_LOCALES

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-neutral-500 dark:text-neutral-300">
          {t`Manage translations for merchandise group labels per locale`}
        </p>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <Select
              value={comparisonVersionId ?? ""}
              onValueChange={(v) => setComparisonVersionId(v || null)}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <GitCompare className="mr-2 size-4 shrink-0" />
                <SelectValue placeholder={t`Compare to...`} />
              </SelectTrigger>
              <SelectContent>
                {otherVersions.map((version) => (
                  <SelectItem key={version.id} value={version.id}>
                    {version.versionLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isComparing && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => setComparisonVersionId(null)}
                title={t`Clear comparison`}
                className="shrink-0"
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
          <Link
            to="/settings/audit"
            search={{ entityType: "store-configuration", entityId: versionId }}
          >
            <Button variant="outline" size="sm">
              <History className="mr-2 size-4" />
              {t`Audit Log`}
            </Button>
          </Link>
        </div>
      </div>

      <TranslationsSection
        versionId={versionId}
        supportedLocales={supportedLocales}
        readOnly={!canUpdate}
        compareVersionId={comparisonVersionId ?? undefined}
      />
    </div>
  )
}
